import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaintenanceService } from '../../../Services/maintenance.service';
import { Equipment } from '../../../Models/equipment.model';
import { MaintenanceRecurrenceType, MaintenanceStatus, User } from '../../../Models/maintenance.model';

export interface MaintenanceFormData {
  customerId: number;
  customerName?: string;
  equipments: Equipment[];
  users: User[];
  type: 'planned' | 'service';
  maintenance?: any; // for edit
}

@Component({
  selector: 'app-maintenance-form',
  templateUrl: './maintenance-form.component.html',
  styleUrls: ['./maintenance-form.component.css']
})
export class MaintenanceFormComponent implements OnInit {
  isPlanned: boolean;
  isEdit: boolean;
  saving = false;

  title = '';
  selectedEquipmentId: number | null = null;
  scheduledDate: Date = new Date();
  dueDate: Date | null = null;
  notes = '';
  assignedUserIds: string[] = [];
  recurrenceType: MaintenanceRecurrenceType = MaintenanceRecurrenceType.None;
  recurrenceIntervalDays: number | null = null;
  notificationsEnabled = true;
  notificationLeadTimeDays = 3;

  recurrenceOptions = [
    { value: MaintenanceRecurrenceType.None, label: 'Einmalig' },
    { value: MaintenanceRecurrenceType.Weekly, label: 'Wöchentlich' },
    { value: MaintenanceRecurrenceType.Monthly, label: 'Monatlich' },
    { value: MaintenanceRecurrenceType.Quarterly, label: 'Vierteljährlich' },
    { value: MaintenanceRecurrenceType.Yearly, label: 'Jährlich' },
    { value: MaintenanceRecurrenceType.Custom, label: 'Benutzerdefiniert' },
  ];

  constructor(
    public dialogRef: MatDialogRef<MaintenanceFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MaintenanceFormData,
    private maintenanceService: MaintenanceService
  ) {
    this.isPlanned = data.type === 'planned';
    this.isEdit = !!data.maintenance?.id;

    if (this.isEdit) {
      const m = data.maintenance;
      this.title = m.title;
      this.selectedEquipmentId = m.equipmentId;
      this.scheduledDate = new Date(m.scheduledDate);
      this.dueDate = m.dueDate ? new Date(m.dueDate) : null;
      this.notes = m.notes ?? '';
      this.assignedUserIds = m.assignedUserIds?.length ? [...m.assignedUserIds] : (m.assignedUserId ? [m.assignedUserId] : []);
      this.recurrenceType = m.recurrenceType;
      this.recurrenceIntervalDays = m.recurrenceIntervalDays;
      this.notificationsEnabled = m.notificationsEnabled;
      this.notificationLeadTimeDays = m.notificationLeadTimeDays;
    }
  }

  ngOnInit(): void {
    if (this.isPlanned && !this.isEdit) {
      this.recurrenceType = MaintenanceRecurrenceType.Monthly;
    }
    if (this.data.equipments?.length === 1) {
      this.selectedEquipmentId = this.data.equipments[0].id;
    }
  }

  get equipments(): Equipment[] {
    return this.data.equipments ?? [];
  }

  get users(): User[] {
    return this.data.users ?? [];
  }

  get isCustomRecurrence(): boolean {
    return this.recurrenceType === MaintenanceRecurrenceType.Custom;
  }

  get customerName(): string {
    return this.data.customerName ?? '';
  }

  /** Hinweis, welches Pflichtfeld noch fehlt (statt nur ausgegrautem Button). */
  get validationHint(): string {
    if (!this.title.trim()) return 'Bitte Titel/Beschreibung eingeben.';
    if (!this.selectedEquipmentId) {
      return this.equipments.length === 0
        ? 'Für diesen Kunden ist noch keine Anlage angelegt.'
        : 'Bitte eine Anlage/Verteiler wählen.';
    }
    return '';
  }

  save(): void {
    if (!this.title.trim() || !this.selectedEquipmentId) return;
    this.saving = true;

    const payload: any = {
      equipmentId: this.selectedEquipmentId,
      title: this.title.trim(),
      scheduledDate: this.scheduledDate.toISOString(),
      dueDate: this.dueDate?.toISOString() ?? null,
      // Beim Bearbeiten Status & Fortschritt der bestehenden Wartung beibehalten,
      // damit das Ändern der Grunddaten eine laufende/erledigte Wartung nicht zurücksetzt.
      status: this.isEdit ? (this.data.maintenance?.status ?? MaintenanceStatus.Planned) : MaintenanceStatus.Planned,
      notes: this.notes || '',
      assignedUserIds: this.assignedUserIds,
      recurrenceType: this.isPlanned ? this.recurrenceType : MaintenanceRecurrenceType.None,
      recurrenceIntervalDays: this.isCustomRecurrence ? this.recurrenceIntervalDays : null,
      notificationsEnabled: this.notificationsEnabled,
      notificationLeadTimeDays: this.notificationLeadTimeDays,
      progressPercent: this.isEdit ? (this.data.maintenance?.progressPercent ?? 0) : 0
    };

    if (this.isEdit) {
      this.maintenanceService.updateMaintenance(this.data.maintenance.id, payload).subscribe({
        next: () => { this.saving = false; this.dialogRef.close(true); },
        error: () => { this.saving = false; }
      });
    } else {
      this.maintenanceService.createMaintenance(payload).subscribe({
        next: () => { this.saving = false; this.dialogRef.close(true); },
        error: () => { this.saving = false; }
      });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
